import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Stethoscope, Phone, MapPin, Mail, Clock, Calendar, Shield,
  Heart, Users, Award, ArrowRight, ChevronRight, Loader2, Pill, FileText, CheckCircle2,
} from 'lucide-react';
import PublicBookingSection from '../components/PublicBookingSection';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Public clinic landing page — the marketing surface for each tenant.
 * Picks up branding (logo, primary color, display name, letterhead, footer)
 * from /api/tenant/resolve and pulls a doctor list from /api/tenant/{slug}/doctors.
 *
 * Reached two ways:
 *   1. /c/<slug> (canonical)
 *   2. Subdomain root (<slug>.your-domain.com/) — when wildcard DNS is wired
 *
 * Design intent: confidence + warmth, not slop. Big serif hero, asymmetric
 * stats band, doctor cards with subtle hover, sticky branded CTA bar.
 */
export default function PublicClinicLandingPage() {
  const { slug: slugParam } = useParams();
  const slug = (slugParam || window.location.hostname.split('.')[0] || '').toLowerCase();
  // Sign-in / book-visit CTAs route to the branded login. Using a hard
  // navigation (vs. react-router's navigate) forces TenantContext to
  // re-mount and pin the slug from the URL — without that, the central
  // picker leaks through when arriving from the public landing.
  const gotoLogin = () => window.location.assign(`/login?tenant=${slug}`);
  const [tenant, setTenant] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    (async () => {
      try {
        const [tRes, dRes] = await Promise.allSettled([
          axios.get(`${API}/api/tenant/resolve`, { params: { slug } }),
          axios.get(`${API}/api/tenant/${slug}/doctors`),
        ]);
        if (cancelled) return;
        if (tRes.status === 'fulfilled') setTenant(tRes.value.data);
        else setError('Clinic not found');
        if (dRes.status === 'fulfilled') setDoctors(dRes.value.data || []);
      } catch (_) {
        if (!cancelled) setError('Clinic not found');
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950 text-zinc-200">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }
  if (error || !tenant) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950 text-zinc-200">
        <div className="text-center">
          <p className="text-lg font-medium">Clinic not found</p>
          <p className="text-sm text-zinc-500 mt-1">Check the URL or contact the clinic.</p>
        </div>
      </div>
    );
  }

  const b = tenant.branding || {};
  const name = b.display_name || tenant.name || 'Our Clinic';
  const primary = b.primary_color || '#0f766e';
  const logo = b.logo_url || b.prescription_logo_url;
  const letterhead = b.letterhead_text || '';
  const lhLines = letterhead.split('\n').filter(Boolean);
  const tagline = b.tagline || '';
  const aboutLong = b.about_long || '';
  const highlights = (b.about_highlights || []).filter(Boolean);
  const heroImage = b.hero_image_url;
  // Light wash for hero gradient + accent flourishes. Custom hero image
  // (if provided) layered behind the gradient.
  const heroStyle = heroImage
    ? {
        backgroundImage:
          `linear-gradient(135deg, rgba(11,11,12,0.85), rgba(11,11,12,0.55)),` +
          ` url("${heroImage}")`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }
    : {
        background:
          `radial-gradient(ellipse at top, ${primary}30 0%, transparent 55%),` +
          ` radial-gradient(ellipse at bottom right, ${primary}25 0%, transparent 50%),` +
          ` #0b0b0c`,
      };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="public-clinic-landing">
      {/* NAV */}
      <nav className="sticky top-0 z-30 backdrop-blur-xl bg-zinc-950/70 border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            {logo ? (
              <img src={logo} alt={name} className="h-7 w-7 rounded-md object-cover" />
            ) : (
              <div className="h-7 w-7 rounded-md flex items-center justify-center text-white font-bold text-xs"
                   style={{ background: primary }}>
                {name[0]?.toUpperCase()}
              </div>
            )}
            <span className="font-semibold text-zinc-100 text-sm tracking-tight">{name}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <a href="#doctors" className="text-xs text-zinc-400 hover:text-zinc-100 px-3 py-1.5 rounded-md transition hidden sm:inline-flex"
               data-testid="nav-doctors">Doctors</a>
            {(aboutLong || highlights.length > 0) && (
              <a href="#about" className="text-xs text-zinc-400 hover:text-zinc-100 px-3 py-1.5 rounded-md transition hidden sm:inline-flex"
                 data-testid="nav-about">About</a>
            )}
            <a href="#services" className="text-xs text-zinc-400 hover:text-zinc-100 px-3 py-1.5 rounded-md transition hidden sm:inline-flex"
               data-testid="nav-services">Services</a>
            <a href="#contact" className="text-xs text-zinc-400 hover:text-zinc-100 px-3 py-1.5 rounded-md transition hidden sm:inline-flex"
               data-testid="nav-contact">Contact</a>
            <button
              onClick={gotoLogin}
              className="text-xs font-medium text-white px-4 py-1.5 rounded-full transition hover:opacity-90"
              style={{ background: primary }}
              data-testid="nav-staff-login"
            >
              Staff sign-in <ChevronRight className="inline h-3 w-3" />
            </button>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative overflow-hidden" style={heroStyle}>
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
             style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '32px 32px' }} />
        <div className="max-w-6xl mx-auto px-6 py-24 sm:py-32 grid lg:grid-cols-12 gap-10 items-center relative">
          <div className="lg:col-span-7 space-y-7">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-white/5 text-[11px] uppercase tracking-widest text-zinc-300">
              <span className="h-1.5 w-1.5 rounded-full animate-pulse" style={{ background: primary }} />
              Now accepting new patients
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tight"
                style={{ fontFamily: 'Manrope, ui-sans-serif' }}>
              Care that feels<br />
              <span style={{ color: primary }}>personal again.</span>
            </h1>
            <p className="text-base text-zinc-300 max-w-xl leading-relaxed">
              {tagline
                ? tagline
                : <>Welcome to <b className="text-zinc-100">{name}</b> — a modern clinic where your appointment isn't a number on a slip, it's a conversation that respects your time and your story.</>}
            </p>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => { const el = document.getElementById('book-section'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-white shadow-xl shadow-black/30 transition hover:scale-[1.02]"
                style={{ background: primary }}
                data-testid="hero-book"
              >
                Book your visit <ArrowRight className="h-4 w-4" />
              </button>
              <a href="#doctors"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium border border-white/15 text-zinc-200 hover:bg-white/5 transition"
                data-testid="hero-doctors">
                Meet our doctors
              </a>
            </div>
            <div className="flex items-center gap-6 pt-4">
              {[
                { icon: Shield, label: 'Your data, your clinic' },
                { icon: CheckCircle2, label: 'Digital prescriptions' },
                { icon: Clock, label: 'No-wait scheduling' },
              ].map(({ icon: Icon, label }) => (
                <div key={label} className="flex items-center gap-1.5 text-[11px] text-zinc-500">
                  <Icon className="h-3.5 w-3.5" /> {label}
                </div>
              ))}
            </div>
          </div>

          {/* Glassy info card */}
          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-1 rounded-3xl blur-2xl opacity-40"
                   style={{ background: `linear-gradient(135deg, ${primary}, transparent 70%)` }} />
              <div className="relative rounded-3xl border border-white/10 bg-zinc-900/60 backdrop-blur-xl p-7 shadow-2xl shadow-black/40">
                <div className="flex items-center gap-3 pb-5 border-b border-white/5">
                  {logo ? (
                    <img src={logo} alt={name} className="h-12 w-12 rounded-xl object-cover" />
                  ) : (
                    <div className="h-12 w-12 rounded-xl flex items-center justify-center text-white text-xl font-bold"
                         style={{ background: primary }}>
                      {name[0]?.toUpperCase()}
                    </div>
                  )}
                  <div>
                    <p className="text-zinc-200 font-semibold text-base leading-tight">{name}</p>
                    <p className="text-[11px] text-zinc-500">{doctors.length || '—'} practising physician{doctors.length === 1 ? '' : 's'}</p>
                  </div>
                </div>
                <div className="space-y-3 pt-5">
                  {lhLines.length > 0 ? lhLines.slice(0, 4).map((line, i) => (
                    <div key={i} className="flex gap-2.5 items-start text-[13px] text-zinc-300 leading-relaxed">
                      {i === 0 && <MapPin className="h-4 w-4 mt-0.5 text-zinc-500 flex-shrink-0" style={{ color: primary }} />}
                      {i === 1 && <Phone className="h-4 w-4 mt-0.5 text-zinc-500 flex-shrink-0" style={{ color: primary }} />}
                      {i === 2 && <Mail className="h-4 w-4 mt-0.5 text-zinc-500 flex-shrink-0" style={{ color: primary }} />}
                      {i >= 3 && <FileText className="h-4 w-4 mt-0.5 text-zinc-500 flex-shrink-0" style={{ color: primary }} />}
                      <span>{line}</span>
                    </div>
                  )) : (
                    <p className="text-[12px] text-zinc-500 italic">
                      Letterhead details will appear here once the clinic admin adds them in branding.
                    </p>
                  )}
                </div>
                <div className="mt-6 grid grid-cols-3 gap-3 pt-5 border-t border-white/5 text-center">
                  {[
                    { v: doctors.length || 'Multi', l: 'Specialists' },
                    { v: '24×7', l: 'Records online' },
                    { v: '0₹', l: 'Booking fee' },
                  ].map(({ v, l }) => (
                    <div key={l}>
                      <p className="text-lg font-bold text-zinc-100">{v}</p>
                      <p className="text-[10px] uppercase tracking-wider text-zinc-500">{l}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DOCTORS */}
      <section id="doctors" className="py-24 bg-zinc-950 border-t border-white/5">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-3">
            <div>
              <p className="text-[11px] uppercase tracking-widest text-zinc-500">The team</p>
              <h2 className="text-3xl sm:text-4xl font-bold mt-2 tracking-tight"
                  style={{ fontFamily: 'Manrope, ui-sans-serif' }}>
                Meet your <span style={{ color: primary }}>doctors</span>
              </h2>
            </div>
            <p className="text-sm text-zinc-500 max-w-md">
              Verified specialists. Pick the one who fits your need — or let our reception
              guide you when you walk in.
            </p>
          </div>
          {doctors.length === 0 ? (
            <div className="border border-white/5 rounded-2xl p-12 text-center text-zinc-500">
              <Stethoscope className="h-8 w-8 mx-auto mb-3 opacity-40" />
              <p className="text-sm">Doctors will appear here once the clinic admin adds them.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {doctors.map((d, i) => (
                <div key={i}
                  className="group relative rounded-2xl p-6 border border-white/5 bg-gradient-to-br from-white/[0.04] to-transparent hover:border-white/15 transition"
                  data-testid={`doctor-card-${i}`}>
                  <div className="flex items-start gap-4">
                    <div className="h-14 w-14 rounded-full flex items-center justify-center text-xl font-bold text-white flex-shrink-0 transition group-hover:scale-110"
                         style={{ background: primary }}>
                      {d.initial}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-zinc-100">{d.name}</p>
                      {d.specialization ? (
                        <p className="text-[12px] text-zinc-400 mt-0.5">{d.specialization}</p>
                      ) : (
                        <p className="text-[12px] text-zinc-500 italic mt-0.5">General consultation</p>
                      )}
                    </div>
                  </div>
                  <div className="mt-5 flex items-center gap-2 text-[11px] text-zinc-500">
                    <Calendar className="h-3 w-3" /> Available this week
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ABOUT — only renders when the admin has written something. */}
      {(aboutLong || highlights.length > 0) && (
        <section id="about" className="py-24 bg-zinc-950 border-t border-white/5">
          <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-7 space-y-6">
              <p className="text-[11px] uppercase tracking-widest text-zinc-500">About us</p>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight leading-tight"
                  style={{ fontFamily: 'Manrope, ui-sans-serif' }}>
                The story behind <span style={{ color: primary }}>{name}</span>
              </h2>
              {aboutLong && (
                <div className="text-zinc-300 leading-relaxed space-y-4 text-[15px]"
                     data-testid="landing-about-long">
                  {aboutLong.split('\n').filter(Boolean).map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
                </div>
              )}
            </div>
            {highlights.length > 0 && (
              <div className="lg:col-span-5">
                <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.05] to-transparent p-7">
                  <p className="text-[11px] uppercase tracking-widest mb-4"
                     style={{ color: primary }}>What you can expect</p>
                  <ul className="space-y-3">
                    {highlights.map((h, i) => (
                      <li key={i} className="flex gap-3 items-start" data-testid={`landing-highlight-${i}`}>
                        <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: primary }} />
                        <span className="text-[14px] text-zinc-200 leading-relaxed">{h}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* SERVICES */}
      <section id="services" className="py-24 border-t border-white/5"
        style={{ background: `linear-gradient(to bottom, transparent, ${primary}08)` }}>
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-[11px] uppercase tracking-widest text-zinc-500">What we do</p>
            <h2 className="text-3xl sm:text-4xl font-bold mt-2 tracking-tight"
                style={{ fontFamily: 'Manrope, ui-sans-serif' }}>
              Full-spectrum care, <span style={{ color: primary }}>under one roof</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { Icon: Stethoscope, t: 'Consultations',
                d: 'Walk-in or appointment. Same-day prescriptions delivered digitally to your phone.' },
              { Icon: Pill, t: 'In-house pharmacy',
                d: 'Pick up your medication on your way out — no extra trip, no mismatch.' },
              { Icon: FileText, t: 'Digital records',
                d: 'Your visits, prescriptions and reports — searchable, exportable, yours forever.' },
              { Icon: Heart, t: 'Family registration',
                d: 'One phone number, one record. Spouse, kids, parents — all linked, all tracked.' },
              { Icon: Award, t: 'Specialist network',
                d: 'Need a referral? Our doctors stay with you through the full care journey.' },
              { Icon: Users, t: 'Reception that knows you',
                d: 'Your name, not a queue number. We greet returning patients by first name.' },
            ].map(({ Icon, t, d }) => (
              <div key={t}
                className="rounded-2xl p-6 border border-white/5 bg-zinc-900/40 hover:bg-zinc-900/70 transition">
                <Icon className="h-5 w-5 mb-4" style={{ color: primary }} />
                <p className="font-semibold text-zinc-100 mb-1.5">{t}</p>
                <p className="text-[13px] text-zinc-400 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="contact" className="py-24 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl sm:text-5xl font-bold tracking-tight"
              style={{ fontFamily: 'Manrope, ui-sans-serif' }}>
            Ready when you are.
          </h2>
          <p className="text-zinc-400 mt-4 max-w-xl mx-auto">
            Booking takes under a minute. Walk in, or schedule a slot — both work.
          </p>
          <div className="mt-10 max-w-3xl mx-auto" id="book-section">
            <PublicBookingSection slug={slug} primary={primary} />
          </div>
          <div className="mt-8 flex justify-center gap-3 flex-wrap">
            <button
              onClick={gotoLogin}
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold text-white shadow-xl transition hover:scale-[1.02]"
              style={{ background: primary }}
              data-testid="cta-book"
            >
              Book your visit <ArrowRight className="h-4 w-4" />
            </button>
            {lhLines[1] && (
              <a href={`tel:${lhLines[1]}`}
                 className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-medium border border-white/15 text-zinc-100 hover:bg-white/5 transition">
                <Phone className="h-4 w-4" /> Call us
              </a>
            )}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-white/5 py-10">
        <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            {logo ? (
              <img src={logo} alt={name} className="h-5 w-5 rounded object-cover" />
            ) : (
              <div className="h-5 w-5 rounded flex items-center justify-center text-white text-[10px] font-bold"
                   style={{ background: primary }}>
                {name[0]?.toUpperCase()}
              </div>
            )}
            <span className="text-xs text-zinc-500">© {new Date().getFullYear()} {name}. All rights reserved.</span>
          </div>
          <div className="text-[11px] text-zinc-600">
            <Link to={`/login?tenant=${slug}`} className="hover:text-zinc-300">Staff sign-in</Link>
            <span className="mx-2">·</span>
            <Link to="/signup" className="hover:text-zinc-300">Run your own clinic on this platform →</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
